import { useWindowIsMobile } from '@liveauctioneers/hammer-ui-core/providers';
import { BodySecondary, H3, LabelMini, LabelStrong } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import { useAppSelector } from '@/redux/hooks';
import {
    getCatalogLotsListed,
    getTimedPlusCatalogClosedLotCount,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { ProgressBar } from './ProgressBar';

type Props = {
    catalogId: number;
    className?: string;
};

const EMPTY_CLOSED_LOTS_COUNT = 0;
const EMPTY_PROGRESS_PERCENT = 0;
const PERCENTAGE_SCALE = 100;

export const TimedPlusInProgressCluster = ({ catalogId, className }: Props) => {
    const formatMessage = useAppFormatMessage();
    const isMobile = useWindowIsMobile();
    const total = useAppSelector((state) => getCatalogLotsListed(state, catalogId));
    const closedLotsCount =
        useAppSelector((state) => getTimedPlusCatalogClosedLotCount(state, catalogId)) ?? EMPTY_CLOSED_LOTS_COUNT;
    const percentage = total ? Math.round((closedLotsCount / total) * PERCENTAGE_SCALE) : EMPTY_PROGRESS_PERCENT;
    const lotsClosedLabel = formatMessage(['catalogPage', 'catalogStatus', 'progressStatus'], {
        values: { current: closedLotsCount, total },
    });

    if (isMobile) {
        return (
            <div
                className={cn('flex w-full flex-col gap-xs rounded-md', className)}
                data-testid="timed-plus-in-progress-cluster"
            >
                <div className="flex w-full items-start gap-xs">
                    <LabelStrong className="whitespace-nowrap text-text-primary">
                        {formatMessage(['catalogPage', 'catalogStatus', 'inProgressLabel'])}
                    </LabelStrong>
                    <BodySecondary className="min-w-0 flex-1 text-right text-text-secondary">
                        {lotsClosedLabel}
                    </BodySecondary>
                </div>
                <ProgressBar percentage={percentage} />
            </div>
        );
    }

    return (
        <div
            className={cn('flex w-full flex-col items-center gap-sm', className)}
            data-testid="timed-plus-in-progress-cluster"
        >
            <LabelMini className="w-full text-center text-text-secondary uppercase">
                {formatMessage(['catalogPage', 'catalogStatus', 'inProgress'])}
            </LabelMini>
            <H3 className="w-full text-center">{lotsClosedLabel}</H3>
            <ProgressBar percentage={percentage} />
        </div>
    );
};
